/**
 * Canonical entity_type strings used across community_entities rows.
 * These match the values in the database's community_entities.entity_type
 * column.
 *
 * One file owns every entity_type string. Importing constants here instead
 * of writing literal strings at call sites means a typo fails to typecheck
 * rather than silently mismatching at runtime.
 *
 * Scope of this file (DEV-920, Layer 1 / B1):
 *  - Phase C surfaces (CommunitySchemasTab, SchemaHierarchyEditor) consume
 *    these constants.
 *  - Existing call sites in other components (GeographyAdminModal,
 *    ProjectConfigModal, KpiGridTable, CommunityEntitiesTab, etc.) keep
 *    their literals until the Layer 5 cleanup sweep.
 *
 * Adding a new entity_type:
 *  1. Add the string to the appropriate group below.
 *  2. (Backend) Migrate any DB seeds that produce the new type.
 *  3. (Backend) Update walk_org_root() / IsOrgRootEntity if the new type
 *     should be treated as an org root for ADR-004 purposes.
 *
 * The list is intentionally non-exhaustive in Layer 1 — extend it during
 * the Layer 5 sweep when the legacy call sites are migrated.
 */

/**
 * Geographic hierarchy entity types. Used for the platform-curated
 * Geographic Hierarchy declared in schema_hierarchies with
 * value_source='entity_walk'.
 *
 * Order follows the geographic containment hierarchy (root → leaf).
 */
export const GEO_ENTITY_TYPES = {
  COUNTRY:      'geo_country',
  STATE:        'geo_state',
  REGION:       'geo_region',
  CITY:         'geo_city',
  DISTRICT:     'geo_district',
  NEIGHBORHOOD: 'geo_neighborhood',
} as const;

/**
 * Organizational hierarchy entity types. The naming convention is
 * 'org_<orgname>_<level>' for org-level rows, 'organization' for the
 * generic org-root marker used by orgs without a structured hierarchy.
 *
 * Backend treats anything matching `entity_type === 'organization' ||
 * entity_type LIKE 'org_%'` as an org root for ADR-004 visibility scoping.
 */
export const ORG_ENTITY_TYPES = {
  /**
   * Generic org-root marker. Used for organizations that don't have a
   * structured hierarchy declared via org_level_taxonomy.
   */
  ORGANIZATION: 'organization',

  /** Rotary International — three-tier structure. */
  ROTARY_ZONE:     'org_rotary_zone',
  ROTARY_DISTRICT: 'org_rotary_district',
  ROTARY_CLUB:     'org_rotary_club',
} as const;

/**
 * Workspace / non-community owners. The solo-creator-workshop pattern
 * (DEV-896 / migration 137) lets a single user own projects without
 * forming a full community_entity hierarchy.
 */
export const WORKSPACE_ENTITY_TYPES = {
  SOLO_CREATOR_WORKSHOP: 'solo_creator_workshop',
} as const;

/**
 * Every entity_type string as a union. Use as the type for function
 * parameters that accept an entity_type, instead of `string`.
 *
 * As constants are added to GEO_/ORG_/WORKSPACE_ above, this union picks
 * them up automatically.
 */
export type EntityType =
  | typeof GEO_ENTITY_TYPES[keyof typeof GEO_ENTITY_TYPES]
  | typeof ORG_ENTITY_TYPES[keyof typeof ORG_ENTITY_TYPES]
  | typeof WORKSPACE_ENTITY_TYPES[keyof typeof WORKSPACE_ENTITY_TYPES];

/**
 * True iff the given entity_type is a geographic-hierarchy type.
 * Use to gate UI that's specific to the platform Geographic Hierarchy
 * (e.g. the Geography admin modal, geo Group By).
 */
export const isGeoEntityType = (
  t: string,
): t is typeof GEO_ENTITY_TYPES[keyof typeof GEO_ENTITY_TYPES] =>
  (Object.values(GEO_ENTITY_TYPES) as readonly string[]).includes(t);

/**
 * True iff the given entity_type is an organizational-hierarchy type.
 * Mirrors the backend IsOrgRootEntity / walk_org_root predicate:
 * `t === 'organization' || t.startsWith('org_')`.
 *
 * Predicate-shape rather than enumeration so future org_<orgname>_<level>
 * types light up automatically without needing to be added here first.
 */
export const isOrgEntityType = (t: string): boolean =>
  t === ORG_ENTITY_TYPES.ORGANIZATION || t.startsWith('org_');

/**
 * True iff the given entity_type is a non-community workspace owner
 * (e.g. solo creator workshop).
 */
export const isWorkspaceEntityType = (
  t: string,
): t is typeof WORKSPACE_ENTITY_TYPES[keyof typeof WORKSPACE_ENTITY_TYPES] =>
  (Object.values(WORKSPACE_ENTITY_TYPES) as readonly string[]).includes(t);

// ============================================================================
// Friendly-label catalog (DEV-1214 iter 2.5, 2026-06-04)
// ============================================================================
//
// Maps each known entity_type to a user-facing label, short description, and
// pillar. Consumed by entity_type pickers (BundleEditTray today, future
// Community Settings creation flow) so admins see "Rotary Club" instead of
// `org_rotary_club`.
//
// This is the SUGGESTION list — the DB CHECK constraint (migration 213) is
// open-ended for `org_*` and `geo_*` prefixes, so callers should also allow
// free-form entry for types not in this list. Free-form values save the raw
// technical key with no label resolution.
//
// LONGER-TERM (DEV-1248, filed 2026-06-04): this catalog moves to a DB-backed
// admin-managed table so sysadmins can add new entity_types via UI instead of
// editing this file. When that ships, this constant becomes the seed data.
//
// Ordering within each pillar: root → leaf (matches existing GEO/ORG enum
// order). Pillars are surfaced in the order [org, geo, project, workspace]
// to match the visual order of the existing Admin Role Catalogue pillar tabs.

/** Friendly metadata for an entity_type. */
export interface EntityTypeMeta {
  /** Raw value stored in community_entities.entity_type. */
  key: string;
  /** User-facing display name. */
  label: string;
  /** One-line description for tooltips / picker subtext. */
  description: string;
  /** Visual + organizational grouping in pickers. */
  pillar: 'org' | 'geo' | 'project' | 'workspace';
}

export const ENTITY_TYPE_CATALOG: readonly EntityTypeMeta[] = [
  // ── Org pillar ──────────────────────────────────────────────────────────
  {
    key: ORG_ENTITY_TYPES.ORGANIZATION,
    label: 'Organization (generic)',
    description: 'Generic org root for organizations without a structured hierarchy.',
    pillar: 'org',
  },
  {
    key: ORG_ENTITY_TYPES.ROTARY_ZONE,
    label: 'Rotary Zone',
    description: 'Top tier of the Rotary International three-tier structure.',
    pillar: 'org',
  },
  {
    key: ORG_ENTITY_TYPES.ROTARY_DISTRICT,
    label: 'Rotary District',
    description: 'Mid tier — groups multiple Rotary clubs in a region.',
    pillar: 'org',
  },
  {
    key: ORG_ENTITY_TYPES.ROTARY_CLUB,
    label: 'Rotary Club',
    description: 'Leaf-tier club entity in the Rotary International hierarchy.',
    pillar: 'org',
  },

  // ── Geo pillar (root → leaf) ────────────────────────────────────────────
  {
    key: GEO_ENTITY_TYPES.COUNTRY,
    label: 'Country',
    description: 'Top-level geographic entity (e.g., United States).',
    pillar: 'geo',
  },
  {
    key: GEO_ENTITY_TYPES.STATE,
    label: 'State / Province',
    description: 'Sub-national geographic entity (e.g., Massachusetts).',
    pillar: 'geo',
  },
  {
    key: GEO_ENTITY_TYPES.REGION,
    label: 'Region',
    description: 'Sub-state or multi-state grouping (e.g., New England).',
    pillar: 'geo',
  },
  {
    key: GEO_ENTITY_TYPES.CITY,
    label: 'City / Town',
    description: 'Municipal geographic entity.',
    pillar: 'geo',
  },
  {
    key: GEO_ENTITY_TYPES.DISTRICT,
    label: 'District',
    description: 'Intra-city administrative division.',
    pillar: 'geo',
  },
  {
    key: GEO_ENTITY_TYPES.NEIGHBORHOOD,
    label: 'Neighborhood',
    description: 'Smallest geographic grain — sub-city / sub-district.',
    pillar: 'geo',
  },

  // ── Project pillar ──────────────────────────────────────────────────────
  // Note: full_project entities live in the projects table, not
  // community_entities. Listed here because Role Bundles can target
  // full_project via applicable_entity_types.
  {
    key: 'full_project',
    label: 'Full Project',
    description: 'Long-form project (multi-phase, structured plan).',
    pillar: 'project',
  },

  // ── Workspace pillar ────────────────────────────────────────────────────
  {
    key: WORKSPACE_ENTITY_TYPES.SOLO_CREATOR_WORKSHOP,
    label: 'Solo Creator Workshop',
    description: 'Single-user project owner without a community hierarchy (DEV-896).',
    pillar: 'workspace',
  },
] as const;

/** Look up a single entity_type's friendly metadata. Returns undefined for
 *  free-form / unknown values not in the catalog. */
export const getEntityTypeMeta = (key: string): EntityTypeMeta | undefined =>
  ENTITY_TYPE_CATALOG.find(m => m.key === key);

/** Display order of pillars in pickers. Matches the Admin Role Catalogue
 *  tab order (Org / Geo / Project / Workspace). */
export const ENTITY_TYPE_PILLAR_ORDER: readonly EntityTypeMeta['pillar'][] = [
  'org', 'geo', 'project', 'workspace',
] as const;

/** Friendly label for a pillar grouping header. */
export const ENTITY_TYPE_PILLAR_LABEL: Record<EntityTypeMeta['pillar'], string> = {
  org:       'Organization',
  geo:       'Geographic',
  project:   'Project',
  workspace: 'Workspace',
};
